import { MoreOutlined } from '@ant-design/icons'
import { NavigationModal } from '@lmc/ui-kit'
import { Dropdown, type MenuProps } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SHIFT, USER } from 'screens/Rostering/constants'
import useRotaPublishedSendEmail from 'screens/Rostering/hooks/useRotaPublishedSendEmail'
import type {
  ResidentResource,
  RotaEventInstance,
  UserResource
} from 'screens/Rostering/types'
import { YEAR_FIRST_DATE_FORMAT } from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'

dayjs.extend(isoWeek)

type ResourceDropdownProps = {
  resource: UserResource | ResidentResource
  type?: 'resident' | 'user' | 'shift'
  metadata?: {
    date?: Date
    isDailyView?: boolean
  }
}

const ResourceDropdown = ({
  resource,
  type,
  metadata
}: ResourceDropdownProps) => {
  const { t } = useTranslation()

  const isUserResource = type === USER

  const isShiftResource = type === SHIFT

  const isResidentResource = type === 'resident'

  const [showSendEmailModal, setShowSendEmailModal] = useState(false)

  const flags = useFeatureFlag({
    flagNames: ['rota_published_email']
  })?.data

  const flagsRotaPublishedEmailEnabled = flags?.rota_published_email?.enabled

  const { sendEmail } = useRotaPublishedSendEmail()

  const userBaseLink = `/organisation/team/${resource.user?.id}`

  const residentsBaseLink = `/residents/${resource.resident?.id}`

  const profileLink = isUserResource
    ? `${userBaseLink}/profile`
    : `${residentsBaseLink}/profile`

  const contractAndPreferencesLink = `${userBaseLink}/contract-and-preferences`
  const absencesLink = `${userBaseLink}/absences`
  const carePackageAndPatternsLink = `${residentsBaseLink}/care-package-and-patterns`
  const importantPeopleLink = `${residentsBaseLink}/important-people`

  const userItems: MenuProps['items'] = [
    {
      label: (
        <Link
          to={`${window.location.origin}${profileLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('PROFILE')}
        </Link>
      ),
      key: profileLink
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${contractAndPreferencesLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('CONTRACT_AND_PREFERENCES')}
        </Link>
      ),
      key: contractAndPreferencesLink
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${absencesLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('ABSENCES')}
        </Link>
      ),
      key: absencesLink
    },
    ...(flagsRotaPublishedEmailEnabled
      ? [
          {
            label: 'Send email reminder',
            key: 'send-email-reminder',
            onClick: () => {
              setShowSendEmailModal(true)
            }
          }
        ]
      : [])
  ]

  const residentItems: MenuProps['items'] = [
    {
      label: (
        <Link
          to={`${window.location.origin}${profileLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('PROFILE')}
        </Link>
      ),
      key: profileLink
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${carePackageAndPatternsLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('CARE_PACKAGE_AND_PATTERNS')}
        </Link>
      ),
      key: carePackageAndPatternsLink
    },
    {
      label: (
        <Link
          to={`${window.location.origin}${importantPeopleLink}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('IMPORTANT_PEOPLE')}
        </Link>
      ),
      key: importantPeopleLink
    }
  ]

  const distinctPatternShifts = resource?.shifts?.filter(
    (shift: RotaEventInstance, index: number, self: RotaEventInstance[]) =>
      shift &&
      !shift.orphaned &&
      self.findIndex((s) => s?.patternId === shift.patternId) === index
  )

  const shiftItems: MenuProps['items'] = [
    ...(distinctPatternShifts?.map((shift: RotaEventInstance) => {
      const rotaPatternLink = `/rota/${shift?.patternId}/shift`
      return {
        label: (
          <Link
            to={`${window.location.origin}${rotaPatternLink}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('VIEW_ROTA_PATTERN')} {shift?.title}{' '}
          </Link>
        ),
        key: rotaPatternLink
      }
    }) || [])
  ]

  const getMenuProps = () => {
    let items

    if (isUserResource) {
      items = userItems
    } else if (isResidentResource) {
      items = residentItems
    } else if (isShiftResource) {
      items = shiftItems
    }

    return { items }
  }

  const menuProps = getMenuProps()

  const getModalContentText = () => {
    const userName = resource.user?.name.first + ' ' + resource.user?.name.last
    const selectedDate = dayjs(metadata?.date)

    if (metadata?.isDailyView) {
      return t('SEND_EMAIL_MODAL_DAY', {
        name: userName,
        date: selectedDate.format('DD MMM YYYY')
      })
    }
    const startOfWeek = selectedDate.startOf('isoWeek')
    const endOfWeek = selectedDate.endOf('isoWeek')

    const formattedDate = `${startOfWeek.format('DD MMM YYYY')} - ${endOfWeek.format('DD MMM YYYY')}`
    return t('SEND_EMAIL_MODAL_WEEK', {
      name: userName,
      date: formattedDate
    })
  }

  const handleSendEmail = () => {
    const selectedDate = dayjs(metadata?.date)

    const fromDate = metadata?.isDailyView
      ? selectedDate.format(YEAR_FIRST_DATE_FORMAT)
      : selectedDate.startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)

    sendEmail({
      carerId: resource.user?.id,
      intervalType: metadata?.isDailyView ? 'daily' : 'weekly',
      rotaDate: fromDate
    })
    setShowSendEmailModal(false)
  }

  return menuProps.items && menuProps.items.length > 0 ? (
    <>
      <Dropdown menu={menuProps as MenuProps} trigger={['hover']}>
        <StyledMoreOutlined />
      </Dropdown>
      <NavigationModal
        modalTitle={t('SEND_EMAIL_MODAL_TITLE')}
        modalContentText={getModalContentText()}
        modalConfirmText={t('CONFIRM')}
        modalCancelText={t('CANCEL')}
        showPrompt={showSendEmailModal}
        confirmNavigation={handleSendEmail}
        cancelNavigation={() => setShowSendEmailModal(false)}
        confirmButtonProps={{
          variant: 'primary'
        }}
        width={500}
      />
    </>
  ) : null
}

const StyledMoreOutlined = styled(MoreOutlined)`
  cursor: pointer;
`

export default ResourceDropdown
